import { useEffect, useRef, useState, useCallback} from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";

interface Note {
  id: number;
  content: string;
  createdAt: string;
}

const KANBAN_TAGS = ["#icebox", "#todo", "#doing", "#done"];

function App() {

  const [notes, setNotes] = useState<Note[]>([]);
  const [input, setInput] = useState<string>("");

  const [panelA, setPanelA] = useState("notes");
  const [panelB, setPanelB] = useState("new");
  const [panelC, setPanelC] = useState("chat");
  const [panelD, setPanelD] = useState("empty");

  const [chatInput, setChatInput] = useState("");

  const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null);

  const [choosingPanel, setChoosingPanel] = useState(false);
  const [sourcePanel, setSourcePanel] = useState<"A" | "B" | "C" | "D" | null>(null);

  const [session, setSession] = useState<Session | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const [loadingNotes, setLoadingNotes] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");

  const chatEndRef = useRef<HTMLDivElement>(null);
  const savedContentRef = useRef<string>("");

  const API_BASE = process.env.REACT_APP_API_URL || "";
  const apiUrl = useCallback((path: string) => `${API_BASE}${path}`, [API_BASE]);

  const filteredNotes = notes.filter((note) => {
    if (searchQuery && !note.content.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const sortedNotes = [...filteredNotes].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const notesDesc = [...filteredNotes].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [sortedNotes, sortedNotes.length]);

  const authHeader = () => {
    if (!session?.access_token) { setError("Please sign in again."); return null; }
    return { Authorization: `Bearer ${session.access_token}`};
  }

  const signIn = async () => {
    if(!email || !password) { setError("Enter email + password"); return;}
    setError("");
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
  };

  const signUp = async () => {
    if(!email || !password) { setError("Enter email + password"); return;}
    setError("");
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) setError(error.message); 
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const refreshNotes = async () => {
    const auth = authHeader();
    if (!auth) return;
    const res = await fetch(apiUrl("/notes"), {
      headers: { ...auth },
    });
    if (res.ok) {
      const fresh = await res.json();
      setNotes(fresh);
    }
  };

  const sendChat = async () => {
    if (!chatInput.trim()) return;

    const auth = authHeader();
    if (!auth) return;

    await fetch(apiUrl("/notes"), {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth },
      body: JSON.stringify({ content: chatInput }),
    });

    setChatInput("");
    await refreshNotes();
  };

  const handleSave = async () => {
    if (!input.trim()) return;

    const auth = authHeader();
    if (!auth) return;

    const response = await fetch(apiUrl("/notes"), {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth },
      body: JSON.stringify({ content: input }),
    });

    await response.json();
    setInput("");
    await refreshNotes();
  };

  const handleDelete = async (id: number) => {
    const auth = authHeader();
    if (!auth) return;

    await fetch(apiUrl(`/notes/${id}`), {
      method: "DELETE",
      headers: { ...auth },
    });
    if (selectedNoteId === id) setSelectedNoteId(null);
    await refreshNotes();
  };

  const handleUpdate = async (id: number, content: string) => {
    if (!content.trim()) return;

    const auth = authHeader();
    if (!auth) return;

    await fetch(apiUrl(`/notes/${id}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...auth },
      body: JSON.stringify({ content: content.trim() }),
    });

    const note = notes.find((n) => n.id === id);
    if (note) savedContentRef.current = note.content;
    await refreshNotes();
  };

  const openEditorIn = (setPanel: (v: string) => void) => {
    if (!choosingPanel) return;
    setPanel("editor");
    setChoosingPanel(false);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const fetchNotes = async () => {
      if (!session?.access_token) return;
      setLoadingNotes(true);
      const res = await fetch(apiUrl("/notes"), {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) setNotes(await res.json());
      setLoadingNotes(false);
    };
    fetchNotes();
  }, [apiUrl, session?.access_token]);

  const renderPanel = (value: string, panelId: "A" | "B" | "C" | "D") => {
    if (value === "notes") {
      return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <h2 style={{ margin: 0, fontSize: 14 }}>Notes</h2>
            <span style={{ fontSize: 11, opacity: 0.3 }}>{notesDesc.length}</span>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {loadingNotes ? (
              <p style={{ fontSize: 12, opacity: 0.4 }}>Loading...</p>
            ) : notesDesc.length === 0 ? (
              <p style={{ fontSize: 12, opacity: 0.4 }}>No notes yet.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {notesDesc.map((note) => (
                  <div
                    key={note.id}
                    onClick={() => {
                      setSelectedNoteId(note.id);
                      setSourcePanel(panelId);
                      setChoosingPanel(true);
                    }}
                    style={{
                      padding: "5px 0",
                      borderBottom: "1px solid var(--surface-border)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 6,
                    }}
                  >
                    <small style={{ fontSize: 9, opacity: 0.25, minWidth: 28, flexShrink: 0, marginTop: 2 }}>
                      {new Date(note.createdAt).toLocaleDateString("de-DE", { day: "2-digit", month: "short" })}
                    </small>
                    <span style={{ fontSize: 12, lineHeight: 1.4, flex: 1 }}>
                      {note.content.length > 150 ? note.content.slice(0, 150) + "..." : note.content}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(note.id); }}
                      style={{ padding: "1px 4px", fontSize: 9, background: "transparent", border: "none", color: "var(--secondary)", opacity: 0.3 }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = "0.3"}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (value === "new") {
      return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
          <h2 style={{ margin: 0, fontSize: 14 }}>Write</h2>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="What's on your mind?"
            style={{ flex: 1, minHeight: 0, resize: "none", fontSize: 14, padding: 10, borderRadius: 8 }}
          />
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ fontSize: 10, opacity: 0.3 }}>Un #tags, #icebox, #todo to organize</span>
            <button onClick={handleSave} style={{ marginLeft: "auto", padding: "6px 16px", fontSize: 13 }}>Save</button>
          </div>
        </div>
      );
    }

    if (value === "chat") {
      const formatTime = (iso: string) => {
        const d = new Date(iso);
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      };

      return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 14 }}>Chat</h2>
          <div className="chat-box" style={{ flex: 1, minHeight: 0, overflowY: "auto"}}>
            {sortedNotes.length === 0 ? (
              <div>[--:--] {'<system>'} no notes yet</div>
            ) : (
              <>
                {sortedNotes.map((note) => (
                  <div
                    key={note.id}
                    className={`chat-line ${selectedNoteId === note.id ? "selected" : ""}`}
                    onClick={() => {
                      setSelectedNoteId(note.id);
                      setSourcePanel(panelId);
                      setChoosingPanel(true);
                    }}
                  >
                    [{new Date(note.createdAt).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })} - {formatTime(note.createdAt)}] {'<you>'} {note.content}
                  </div>
                ))}
                <div ref={chatEndRef} />
              </>
            )}
          </div>
          <input
            className="chat-input"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
            placeholder=""
            style={{ padding: 10, borderRadius: 0, border: "1px solid var(--border)"}}
          />
        </div>
      );
    }

    if (value === "search") {
      return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 14 }}>Search</h2>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search notes..."
            style={{ padding: 8, borderRadius: 8, border: "1px solid var(--surface-border)", background: "var(--surface)", color: "var(--text)", outline: "none", fontSize: 13 }}
            autoFocus
          />
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {searchQuery && notesDesc.length === 0 && <p style={{ fontSize: 12, opacity: 0.4 }}>No matches.</p>}
            {searchQuery && notesDesc.map((note) => (
              <div
                key={note.id}
                style={{ padding: "5px 8px", borderBottom: "1px solid var(--surface-border)", cursor: "pointer", fontSize: 12 }}
                onClick={() => {
                  setSelectedNoteId(note.id);
                  setSourcePanel(panelId);
                  setChoosingPanel(true);
                }}
              >
                <div style={{ overflowWrap: "anywhere", lineHeight: 1.4 }}>
                  {note.content.split(new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')).map((part, i) =>
                    part.toLowerCase() === searchQuery.toLowerCase()
                      ? <span key={i} style={{ background: "var(--secondary)", color: "#000", borderRadius: 2, padding: "0 2px" }}>{part}</span>
                      : part
                  )}
                </div>
                <small style={{ fontSize: 9, opacity: 0.3 }}>{new Date(note.createdAt).toLocaleDateString("de-DE", { day: "2-digit", month: "short" })}</small>
              </div>
            ))}
            {!searchQuery && <p style={{ fontSize: 12, opacity: 0.4 }}>Type to search...</p>}
          </div>
        </div>
      );
    }

    if (value === "timeline") {
      const groups = new Map<string, Note[]>();
      for (const n of notesDesc) {
        const key = new Date(n.createdAt).toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(n);
      }

      return (
        <div style={{ height: "100%", overflowY: "auto", padding: "0 4px" }}>
          <h2 style={{ margin: "0 0 8px", fontSize: 14 }}>Timeline</h2>
          {notes.length === 0 ? (
            <p style={{ fontSize: 12, opacity: 0.4 }}>No notes yet.</p>
          ) : (
            <div style={{ position: "relative", paddingLeft: 16 }}>
              <div style={{ position: "absolute", left: 5, top: 0, bottom: 0, width: 1, background: "var(--surface-border)" }} />
              {[...groups.entries()].map(([day, dayNotes]) => (
                <div key={day} style={{ marginBottom: 10, position: "relative" }}>
                  <div style={{ position: "absolute", left: -13, top: 3, width: 7, height: 7, borderRadius: "50%", background: "var(--primary)" }} />
                  <div style={{ fontSize: 10, opacity: 0.35, marginBottom: 3 }}>{day}</div>
                  {dayNotes.map((note) => (
                    <div
                      key={note.id}
                      onClick={() => {
                        setSelectedNoteId(note.id);
                        setSourcePanel(panelId);
                        setChoosingPanel(true);
                      }}
                      style={{ padding: "3px 6px", borderLeft: "2px solid transparent", cursor: "pointer", fontSize: 12, lineHeight: 1.4, marginBottom: 1 }}
                      onMouseEnter={(e) => e.currentTarget.style.borderLeftColor = "var(--primary)"}
                      onMouseLeave={(e) => e.currentTarget.style.borderLeftColor = "transparent"}
                    >
                      <span style={{ fontSize: 9, opacity: 0.25, marginRight: 6 }}>
                        {new Date(note.createdAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {note.content}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (value === "kanban") {
      const columns = KANBAN_TAGS.map((tag) => ({
        tag,
        notes: notesDesc.filter((n) => n.content.toLowerCase().includes(tag)),
      }));

      return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <h2 style={{ margin: "0 0 8px", fontSize: 14 }}>Kanban</h2>
          <div style={{ flex: 1, minHeight: 0, overflowX: "auto", overflowY: "hidden" }}>
            <div style={{ display: "flex", gap: 8, height: "100%" }}>
              {columns.map(({ tag, notes: colNotes }) => (
                <div
                  key={tag}
                  style={{
                    width: 140,
                    flexShrink: 0,
                    background: "var(--surface)",
                    border: "1px solid var(--surface-border)",
                    borderRadius: 8,
                    padding: 8,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.5, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {tag} <span style={{ opacity: 0.3 }}>{colNotes.length}</span>
                  </div>
                  <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
                    {colNotes.length === 0 && <p style={{ fontSize: 10, opacity: 0.2 }}>Empty</p>}
                    {colNotes.map((note) => (
                      <div
                        key={note.id}
                        onClick={() => {
                          setSelectedNoteId(note.id);
                          setSourcePanel(panelId);
                          setChoosingPanel(true);
                        }}
                        style={{
                          padding: "5px 7px",
                          background: "var(--bg)",
                          borderRadius: 5,
                          cursor: "pointer",
                          fontSize: 11,
                          lineHeight: 1.3,
                          border: "1px solid var(--surface-border)",
                        }}
                      >
                        <div style={{ overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }as any}>
                          {note.content.replace(/#[a-zA-Z0-9äöüßæøå_-]+/g, "").trim() || note.content}
                        </div>
                        <div style={{ fontSize: 9, opacity: 0.25, marginTop: 2 }}>
                          {new Date(note.createdAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (value === "stats") {
      const totalNotes = notes.length;
      const allWords = notes.flatMap((n) =>
        n.content.toLowerCase().replace(/[^a-z0-9äöüßæøåéèêëàâùûüîïôœ'`-]/g, " ").split(/\s+/).filter(Boolean)
      );
      const totalWords = allWords.length;

      if (totalNotes === 0) {
        return <div style={{ padding: 8, fontSize: 12, opacity: 0.4 }}>No data yet.</div>;
      }

      // Per-day word count
      const today = new Date();
      const dayData: { label: string; wc: number; count: number }[] = [];
      for (let i = 29; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = d.toLocaleDateString("de-DE");
        const label = d.toLocaleDateString("de-DE", { day: "numeric", month: "short" });
        const dayNotes = notes.filter((n) => new Date(n.createdAt).toLocaleDateString("de-DE") === key);
        dayData.push({
          label,
          wc: dayNotes.reduce((s, n) => s + n.content.split(/\s+/).filter(Boolean).length, 0),
          count: dayNotes.length,
        });
      }
      const maxWc = Math.max(1, ...dayData.map((d) => d.wc));

      // Busiest hours
      const hours = Array(24).fill(0);
      for (const n of notes) hours[new Date(n.createdAt).getHours()]++;
      const maxHour = Math.max(1, ...hours);

      // Top words
      const freq = new Map<string, number>();
      for (const w of allWords) freq.set(w, (freq.get(w) || 0) + 1);
      const topWords = [...freq.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);
      const maxFreq = topWords.length > 0 ? topWords[0][1] : 1;

      // Tag distribution
      const tagCounts = new Map<string, number>();
      for (const n of notes) {
        const matches = n.content.match(/#[a-zA-Z0-9äöüßæøå_-]+/g);
        if (matches) for (const t of matches) tagCounts.set(t.toLowerCase(), (tagCounts.get(t.toLowerCase()) || 0) + 1);
      }
      const topTags = [...tagCounts.entries()].sort((a, b) => b[1] - a[1]);

      return (
        <div style={{ padding: 8, height: "100%", overflowY: "auto", fontSize: 12 }}>
          <h2 style={{ margin: "0 0 10px", fontSize: 14 }}>Stats</h2>

          {/* KPI row */}
          <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--primary)" }}>{totalNotes}</div>
              <div style={{ fontSize: 9, opacity: 0.35 }}>Notes</div>
            </div>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--primary)" }}>{totalWords}</div>
              <div style={{ fontSize: 9, opacity: 0.35 }}>Words</div>
            </div>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--primary)" }}>{Math.round(totalWords / totalNotes)}</div>
              <div style={{ fontSize: 9, opacity: 0.35 }}>W/note</div>
            </div>
          </div>

          {/* 30-day activity chart */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 3, opacity: 0.4 }}>Words / day (30 days)</div>
            <div style={{ display: "flex", gap: 1, alignItems: "flex-end", height: 32 }}>
              {dayData.map((d) => (
                <div key={d.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1, position: "relative" }}>
                  <div style={{
                    width: "100%",
                    height: `${Math.max(2, (d.wc / maxWc) * 28)}px`,
                    background: d.wc > 0 ? "var(--primary)" : "var(--surface)",
                    borderRadius: "1px 1px 0 0",
                    opacity: d.wc > 0 ? 0.7 : 0.3,
                  }} />
                </div>
              ))}
            </div>
          </div>

          {/* Hour heatmap */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 3, opacity: 0.4 }}>Hourly activity</div>
            <div style={{ display: "flex", gap: 1 }}>
              {hours.map((h, i) => (
                <div key={i} style={{
                  flex: 1,
                  height: 8,
                  borderRadius: 1,
                  background: `color-mix(in srgb, var(--primary) ${(h / maxHour) * 100}%, var(--surface))`,
                  opacity: h > 0 ? 0.6 : 0.1,
                }} title={`${i}:00 — ${h} notes`} />
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 1 }}>
              <span style={{ fontSize: 7, opacity: 0.2 }}>0:00</span>
              <span style={{ fontSize: 7, opacity: 0.2 }}>12:00</span>
              <span style={{ fontSize: 7, opacity: 0.2 }}>23:00</span>
            </div>
          </div>

          {/* Tags */}
          {topTags.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 3, opacity: 0.4 }}>Tags</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                {topTags.map(([tag, count]) => (
                  <span key={tag} style={{ padding: "1px 6px", borderRadius: 6, fontSize: 10, background: "var(--surface)", border: "1px solid var(--surface-border)" }}>
                    {tag} <strong>{count}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Top words */}
          {topWords.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 3, opacity: 0.4 }}>Top words</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {topWords.map(([word, count]) => (
                  <div key={word} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ minWidth: 40, fontSize: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{word}</span>
                    <div style={{ flex: 1, height: 6, background: "var(--surface)", borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ width: `${(count / maxFreq) * 100}%`, height: "100%", background: "var(--secondary)", borderRadius: 2 }} />
                    </div>
                    <span style={{ minWidth: 16, textAlign: "right", fontSize: 9, opacity: 0.5 }}>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (value === "editor") {
      const note = selectedNoteId != null ? notes.find((n) => n.id === selectedNoteId) : null;
      if (!note) return <p style={{ padding: 16, fontSize: 12, opacity: 0.5 }}>Click a message to open it here.</p>;

      if (!savedContentRef.current) savedContentRef.current = note.content;

      const handleRevert = () => {
        setNotes((prev) => prev.map((n) => n.id === note.id ? { ...n, content: savedContentRef.current } : n));
      };

      return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ margin: 0, fontSize: 14 }}>Editor</h2>
            <button onClick={handleRevert} style={{ padding: "3px 8px", cursor: "pointer", fontSize: 10 }} title="Revert to last saved version">Revert</button>
          </div>
          <textarea
            key={note.id + savedContentRef.current}
            defaultValue={note.content}
            onBlur={(e) => {
              const newContent = (e.target as HTMLTextAreaElement).value;
              if (newContent !== note.content) {
                savedContentRef.current = newContent;
                handleUpdate(note.id, newContent);
              }
            }}
            style={{ flex: 1, minHeight: 0, padding: 8, borderRadius: 0, border: "1px solid var(--border)", resize: "none", fontFamily: "inherit", fontSize: "inherit" }}
          />
        </div>
      )
    };

    return <div style={{ padding: 16, fontSize: 12, opacity: 0.5 }}>Select a panel view above</div>;
  }

  if (!session) {
    return (
      <div className="auth-screen">
        <div className="auth-grid-preview">
          <div className="preview-panel">
            <div className="preview-header">Chat</div>
            <div className="preview-line short" />
            <div className="preview-line medium" />
            <div className="preview-line long" />
            <div className="preview-line short" />
          </div>
          <div className="preview-panel">
            <div className="preview-header">Stats</div>
            <div className="preview-bar-row">
              <div className="preview-bar" style={{ width: "80%" }} />
              <div className="preview-bar" style={{ width: "50%" }} />
              <div className="preview-bar" style={{ width: "65%" }} />
            </div>
            <div className="preview-heatmap">
              <div className="preview-dot" /><div className="preview-dot" /><div className="preview-dot" />
              <div className="preview-dot" /><div className="preview-dot" /><div className="preview-dot" />
              <div className="preview-dot" />
            </div>
          </div>
          <div className="preview-panel">
            <div className="preview-header">Notes</div>
            <div className="preview-card" />
            <div className="preview-card" style={{ width: "90%" }} />
            <div className="preview-card" style={{ width: "75%" }} />
          </div>
          <div className="preview-panel">
            <div className="preview-header">Editor</div>
            <div className="preview-line long" />
            <div className="preview-line medium" />
            <div className="preview-cursor" />
          </div>
        </div>
        <div className="auth-hero">
          <p className="tagline">Multi-panel note console</p>
          <h1>Bikenar</h1>
          <p className="value-prop">A console for your mind — not a filing cabinet. Four panels, infinite perspectives on your notes.</p>
          <div className="features">
            <div className="feat">Chat — stream of thought, chronological</div>
            <div className="feat">Editor — click any message to deep-edit</div>
            <div className="feat">Search — find notes instantly with highlights</div>
            <div className="feat">Timeline — notes arranged by day</div>
            <div className="feat">Kanban — #icebox #todo #doing #done</div>
            <div className="feat">4 independent panels, any view each</div>
          </div>
          <button className="btn-ghost" onClick={signUp}>Create your space</button>
        </div>
        <div className="auth-card">
          <h2>Sign in</h2>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
          <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" type="password" />
          {error ? <div className="auth-error">{error}</div> : null}
          <div className="auth-actions">
            <button onClick={signIn}>Sign in</button>
            <button className="btn-secondary" onClick={signUp}>Sign up</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h1 style={{ margin: 0 }}>Bikenar'a yaz!</h1>
        <button onClick={signOut} style={{ padding: "4px 12px", cursor: "pointer" }}>Sign out</button>
      </div>

      <div className={`grid ${choosingPanel ? "choose" : ""}`}>
        {(["A","B","C","D"] as const).map((id) => {
          const panelValue = id === "A" ? panelA : id === "B" ? panelB : id === "C" ? panelC : panelD;
          const setPanel = id === "A" ? setPanelA : id === "B" ? setPanelB : id === "C" ? setPanelC : setPanelD;
          return (
            <div key={id} className={`panel panel-${id} ${choosingPanel && sourcePanel === id ? "source-panel" : ""}`}>
              <div className="panel-header">
                <span className={`panel-title ${panelValue === "chat" ? "secondary" : ""}`}>{panelValue}</span>
                <select value={panelValue} onChange={(e) => setPanel(e.target.value)}>
                  <option value="notes">Notes</option>
                  <option value="new">New Note</option>
                  <option value="chat">Chat</option>
                  <option value="search">Search</option>
                  <option value="editor">Editor</option>
                  <option value="stats">Stats</option>
                  <option value="timeline">Timeline</option>
                  <option value="kanban">Kanban</option>
                </select>
              </div>
              <div className="panel-body" onClick={() => openEditorIn(setPanel)}>
                {renderPanel(panelValue, id)}
              </div>
            </div>
          );
        })}
      </div>
    </main>
  );
}

export default App;